import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Check, Save } from 'lucide-react';
import { useAdminPlans, useUpdateAdminPlan, useSaveAdminFeatures } from '@/api/queries/subscription.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

const PLAN_COLOR = { BASIC: '#6C6C70', PRO: '#007AFF', CUSTOM: '#AF52DE' };

function PlanColumn({ plan, featureKeys, onSave, saving }) {
  const [form, setForm] = useState(() => toFormState(plan));
  useEffect(() => { setForm(toFormState(plan)); }, [plan]);

  function toFormState(p) {
    const featureMap = Object.fromEntries(p.features.map(f => [f.feature_key, f.enabled]));
    return {
      name: p.name, tagline: p.tagline || '',
      price_per_employee: p.price_per_employee ?? '',
      employee_limit: p.employee_limit ?? '',
      lens_device_limit: p.lens_device_limit ?? '',
      company_limit: p.company_limit ?? '',
      is_self_serve: p.is_self_serve,
      features: featureMap,
    };
  }

  const numOrNull = (v) => (v === '' ? null : Number(v));
  const inputCls = 'w-full h-9 px-2.5 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[8px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';

  const handleSave = () => {
    onSave(plan.id, {
      patch: {
        name: form.name, tagline: form.tagline,
        price_per_employee: numOrNull(form.price_per_employee),
        employee_limit: numOrNull(form.employee_limit),
        lens_device_limit: numOrNull(form.lens_device_limit),
        company_limit: numOrNull(form.company_limit),
      },
      features: featureKeys.map(key => ({ feature_key: key, enabled: !!form.features[key] })),
    });
  };

  return (
    <div className="card flex flex-col gap-5 p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PLAN_COLOR[plan.code] }} />
          <p className="text-[15px] font-black text-[var(--text-primary)] tracking-wide">{plan.code}</p>
        </div>
        {!form.is_self_serve && <span className="text-[10px] font-bold uppercase text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded-full">Sales-assisted</span>}
      </div>

      <div className="space-y-3.5">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-[var(--text-secondary)]">Display name</label>
          <input className={inputCls} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-[var(--text-secondary)]">Tagline</label>
          <input className={inputCls} value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-[var(--text-secondary)]">₹/employee/mo</label>
            <input className={inputCls} type="number" placeholder="Custom" value={form.price_per_employee} onChange={e => setForm(f => ({ ...f, price_per_employee: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-[var(--text-secondary)]">Employee limit</label>
            <input className={inputCls} type="number" placeholder="Unlimited" value={form.employee_limit} onChange={e => setForm(f => ({ ...f, employee_limit: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-[var(--text-secondary)]">Lens device limit</label>
            <input className={inputCls} type="number" placeholder="Unlimited" value={form.lens_device_limit} onChange={e => setForm(f => ({ ...f, lens_device_limit: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-semibold text-[var(--text-secondary)]">No. of companies</label>
            <input className={inputCls} type="number" placeholder="Unlimited" value={form.company_limit} onChange={e => setForm(f => ({ ...f, company_limit: e.target.value }))} />
          </div>
        </div>
      </div>

      <div className="border-t border-[var(--border)] pt-4 space-y-0.5 max-h-[360px] overflow-y-auto -mx-1.5 px-1.5">
        {featureKeys.map(key => (
          <label key={key} className="flex items-center gap-2.5 text-[12px] text-[var(--text-secondary)] cursor-pointer py-1.5 px-2 -mx-2 rounded-[8px] hover:bg-[var(--bg-hover)] transition-colors">
            <input type="checkbox" checked={!!form.features[key]}
              onChange={e => setForm(f => ({ ...f, features: { ...f.features, [key]: e.target.checked } }))}
              className="w-4 h-4 rounded accent-[var(--accent)] shrink-0" />
            {key}
          </label>
        ))}
      </div>

      <Button onClick={handleSave} disabled={saving} className="w-full" size="sm">
        <Save size={13} /> {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </div>
  );
}

export default function SuperAdminPlanManagementPage() {
  const { data, isLoading } = useAdminPlans();
  const { mutate: updatePlan } = useUpdateAdminPlan();
  const { mutate: saveFeatures } = useSaveAdminFeatures();
  const [savingId, setSavingId] = useState(null);

  const plans = (data?.data ?? []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const featureKeys = plans[0]?.features?.map(f => f.feature_key) ?? [];

  const handleSave = (planId, { patch, features }) => {
    setSavingId(planId);
    updatePlan({ id: planId, ...patch }, {
      onSuccess: () => {
        saveFeatures({ plan_id: planId, features }, {
          onSuccess: () => { toast.success('Plan updated — live everywhere immediately'); setSavingId(null); },
          onError: () => { toast.error('Failed to save features'); setSavingId(null); },
        });
      },
      onError: () => { toast.error('Failed to save plan'); setSavingId(null); },
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-6xl">
      <PageHeader
        title="Plan Management"
        subtitle="Edit pricing, limits and feature toggles — changes apply instantly across the dashboard and the public pricing page"
        breadcrumb={[{ label: 'Super Admin', href: '/super-admin' }, { label: 'Plan Management' }]}
      />

      {isLoading ? (
        <p className="text-[13px] text-[var(--text-secondary)]">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {plans.map(plan => (
            <PlanColumn key={plan.id} plan={plan} featureKeys={featureKeys}
              onSave={handleSave} saving={savingId === plan.id} />
          ))}
        </div>
      )}

      <div className="card flex items-start gap-3 p-5">
        <Check size={16} className="text-green-500 shrink-0 mt-0.5" />
        <p className="text-[12px] text-[var(--text-secondary)]">
          Toggling a feature off here immediately locks it for every company on that plan — no redeploy needed. The same data drives the public marketing website's pricing table.
        </p>
      </div>
    </div>
  );
}
